import { openDB, deleteDB, wrap, unwrap, type IDBPDatabase } from 'idb';
import { devlog } from '../../util/utils.ts';
import type { Project, ProjectID, AppID } from '../../data/projectManagement.ts';
import { internalData } from '../../store/store.ts';
import { envReady } from '../../data/envRegistry.ts';
import { getPrefsBackend, type PrefsBackend } from '../host/prefsBackend.ts';
import type { MeshType } from '../../data/data-interfaces.ts';

// Define types for our database operations
type MeshID = string;

// Default value for when no previous app exists
const NO_PREVIOUS_APP: AppID = 'none';

/**
 * Data structure for transferring or saving mesh data
 * Used when saving a mesh for transfer
 */
export interface TransferSaveData {
  authorId: string;
  meshName: string;
  meshId: MeshID;
  currentApp: AppID;  // The app this mesh is currently in
  destinationApp: AppID;  // The app the mesh is being sent to
  stlData: Blob;
  /** Format of stlData. Senders from 0.19 on set it; a record without it is
   *  STL. The blob keeps the name stlData so an unpatched app still finds it. */
  fileType?: MeshType;
}

/**
 * Complete mesh data as stored in the database
 * Includes additional fields like id and lastSav. 'currentApp' gets turned into 'lastApp' here.
 */
export interface StoredMeshData extends Omit<TransferSaveData, 'currentApp'> {
  id: number;
  lastSave: Date;
  lastApp: AppID;  // The app the mesh was last in
  autoLoad?: boolean;  // Whether to automatically load this mesh
  queueDelete?: boolean; // Whether this mesh should be deleted after loading
}

// =============================================================================
// Database Configuration
// =============================================================================
// Transfer database: 'appsuite' v1 - shared across apps for mesh transfers
// This MUST stay at v1 forever for backwards compatibility with unpatched apps
const TRANSFER_DB_VERSION = 1;
const TRANSFER_DB_NAME = 'appsuite';
const MESH_STORE_NAME = 'meshes';
const META_STORE_NAME = 'meta';

// Schema version stored inside the database (separate from IndexedDB version)
// Increment this when the data format changes, even if IndexedDB version stays at 1
const SCHEMA_VERSION = 1;

// Local database: 'appsuite-local' - app-specific data like projects
// This can evolve independently since it's not shared across app versions
const LOCAL_DB_VERSION = 1;
const LOCAL_DB_NAME = 'appsuite-local';
const PROJECTS_STORE_NAME = 'projects';

// Prefs database: 'appsuite-prefs' - its own database rather than a store in
// appsuite-local, because adding a store there means bumping that database's
// version, and older app builds on the same origin still open it at v1. They'd
// get a VersionError and lose their local projects (the transfer DB carries a
// whole migrate-down routine for exactly that hazard). A separate database
// costs nothing and can't disturb anyone.
const PREFS_DB_VERSION = 1;
const PREFS_DB_NAME = 'appsuite-prefs';
const PREFS_STORE_NAME = 'prefs';

/**
 * Initializes the transfer database for cross-app mesh sharing.
 * Uses 'appsuite' at version 1 - MUST stay at v1 for backwards compatibility.
 * Unpatched older apps (v0.7.0) expect DB_VERSION = 1.
 *
 * If the database is at a higher version (from newer builds), this will:
 * 1. Export existing meshes
 * 2. Delete the database
 * 3. Recreate at v1
 * 4. Restore the meshes
 */
export const initTransferDB = async (): Promise<IDBPDatabase> => {
  devlog('[DB] Initializing transfer database:', TRANSFER_DB_NAME, 'v' + TRANSFER_DB_VERSION);
  try {
    const db = await openDB(TRANSFER_DB_NAME, TRANSFER_DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        devlog('[DB] Transfer DB upgrading from version', oldVersion, 'to', newVersion);

        if (!db.objectStoreNames.contains(MESH_STORE_NAME)) {
          devlog('[DB] Creating mesh store');
          const store = db.createObjectStore(MESH_STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('meshId', 'meshId', { unique: true });
          store.createIndex('lastSave', 'lastSave', { unique: false });
        }

        if (!db.objectStoreNames.contains(META_STORE_NAME)) {
          devlog('[DB] Creating meta store');
          db.createObjectStore(META_STORE_NAME, { keyPath: 'key' });
        }
      }
    });
    // Store schema version
    await storeSchemaVersion(db);
    return db;
  } catch (error) {
    // Database is at a higher version than v1 - need to migrate down for backwards compatibility
    if (error instanceof DOMException && error.name === 'VersionError') {
      devlog('[DB] Transfer DB version is higher than v1, migrating down for backwards compatibility');
      return await migrateTransferDBToV1();
    }
    throw error;
  }
};

/**
 * Stores the current schema version in the meta store.
 */
async function storeSchemaVersion(db: IDBPDatabase): Promise<void> {
  if (!db.objectStoreNames.contains(META_STORE_NAME)) {
    return; // Old database without meta store, skip
  }
  const tx = db.transaction(META_STORE_NAME, 'readwrite');
  const store = tx.objectStore(META_STORE_NAME);
  await store.put({ key: 'schemaVersion', value: SCHEMA_VERSION, updatedAt: new Date() });
  await tx.done;
  devlog('[DB] Stored schema version:', SCHEMA_VERSION);
}

/**
 * Gets the schema version from the database, if available.
 */
export async function getSchemaVersion(): Promise<number | null> {
  const database = await transferDb;
  if (!database) return null;
  if (!database.objectStoreNames.contains(META_STORE_NAME)) {
    return null;
  }
  const tx = database.transaction(META_STORE_NAME, 'readonly');
  const store = tx.objectStore(META_STORE_NAME);
  const record = await store.get('schemaVersion');
  return record?.value ?? null;
}

/**
 * Migrates the transfer database down to v1 for backwards compatibility.
 * Exports meshes, deletes DB, recreates at v1, restores meshes.
 */
async function migrateTransferDBToV1(): Promise<IDBPDatabase> {
  devlog('[DB] Starting migration to v1...');

  // 1. Open at existing version to read data
  const oldDb = await openDB(TRANSFER_DB_NAME);
  const oldVersion = oldDb.version;
  devlog('[DB] Current version:', oldVersion);

  // 2. Export meshes if the store exists
  let meshes: any[] = [];
  if (oldDb.objectStoreNames.contains(MESH_STORE_NAME)) {
    const tx = oldDb.transaction(MESH_STORE_NAME, 'readonly');
    const store = tx.objectStore(MESH_STORE_NAME);
    meshes = await store.getAll();
    devlog('[DB] Exported', meshes.length, 'meshes');
  }

  // 3. Close and delete the database
  oldDb.close();
  await deleteDB(TRANSFER_DB_NAME);
  devlog('[DB] Deleted old database');

  // 4. Create fresh database at v1 with meta store
  const newDb = await openDB(TRANSFER_DB_NAME, TRANSFER_DB_VERSION, {
    upgrade(db) {
      devlog('[DB] Creating fresh v1 database');
      const store = db.createObjectStore(MESH_STORE_NAME, { keyPath: 'id', autoIncrement: true });
      store.createIndex('meshId', 'meshId', { unique: true });
      store.createIndex('lastSave', 'lastSave', { unique: false });
      db.createObjectStore(META_STORE_NAME, { keyPath: 'key' });
    }
  });

  // 5. Store schema version
  await storeSchemaVersion(newDb);

  // 6. Restore meshes (without the old 'id' to let autoIncrement assign new ones)
  if (meshes.length > 0) {
    const tx = newDb.transaction(MESH_STORE_NAME, 'readwrite');
    const store = tx.objectStore(MESH_STORE_NAME);
    for (const mesh of meshes) {
      // Remove old id so autoIncrement assigns a new one
      const { id, ...meshWithoutId } = mesh;
      await store.add(meshWithoutId);
    }
    await tx.done;
    devlog('[DB] Restored', meshes.length, 'meshes to v1 database');
  }

  devlog('[DB] Migration to v1 complete');
  return newDb;
}

/**
 * Initializes the local database for app-specific data (projects, settings, etc.)
 * Uses 'appsuite-local' - separate from transfer DB so it can evolve independently.
 */
export const initLocalDB = async (): Promise<IDBPDatabase> => {
  devlog('[DB] Initializing local database:', LOCAL_DB_NAME, 'v' + LOCAL_DB_VERSION);
  return await openDB(LOCAL_DB_NAME, LOCAL_DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      devlog('[DB] Local DB upgrading from version', oldVersion, 'to', newVersion);

      if (!db.objectStoreNames.contains(PROJECTS_STORE_NAME)) {
        devlog('[DB] Creating projects store');
        const projectStore = db.createObjectStore(PROJECTS_STORE_NAME, { keyPath: 'id' });
        projectStore.createIndex('appId', 'appId', { unique: false });
        projectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

    }
  });
};

/**
 * Initializes the prefs database — small key/value settings that outlive the
 * session. Separate from the local DB on purpose; see PREFS_DB_NAME.
 */
export const initPrefsDB = async (): Promise<IDBPDatabase> => {
  devlog('[DB] Initializing prefs database:', PREFS_DB_NAME, 'v' + PREFS_DB_VERSION);
  return await openDB(PREFS_DB_NAME, PREFS_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(PREFS_STORE_NAME)) {
        devlog('[DB] Creating prefs store');
        db.createObjectStore(PREFS_STORE_NAME, { keyPath: 'key' });
      }
    }
  });
};

// Check if IndexedDB is available (disabled in e.g. iOS Lockdown Mode)
const DB_AVAILABLE = (() => {
  try { return typeof indexedDB !== 'undefined' && indexedDB !== null; } catch { return false; }
})();

if (!DB_AVAILABLE) {
  devlog('[DB] IndexedDB unavailable — all DB operations will be no-ops');
  internalData.noDb = true;
}

const transferDb: Promise<IDBPDatabase | null> = DB_AVAILABLE
  ? initTransferDB().catch(err => { console.warn('[DB] Transfer DB init failed:', err); return null; })
  : Promise.resolve(null);

const localDb: Promise<IDBPDatabase | null> = DB_AVAILABLE
  ? initLocalDB().catch(err => { console.warn('[DB] Local DB init failed:', err); return null; })
  : Promise.resolve(null);

const prefsDb: Promise<IDBPDatabase | null> = DB_AVAILABLE
  ? initPrefsDB().catch(err => { console.warn('[DB] Prefs DB init failed:', err); return null; })
  : Promise.resolve(null);

// Legacy alias for backwards compatibility with code that imports 'db'
export const db = transferDb;

/**
 * Stores or updates a mesh in the database
 * If a mesh with the same meshId exists, it will be updated
 * Otherwise, a new mesh entry will be created
 * @param meshData - The mesh data to store
 * @param autoLoad - Optional flag to indicate if this mesh should be automatically loaded
 */
export const storeMesh = async (meshData: TransferSaveData, autoLoad?: boolean): Promise<StoredMeshData> => {
  if (!(await transferDb)) throw new Error('[DB] IndexedDB unavailable');
  // Validation: Check if lastApp and destinationApp are the same (which indicates an error)
  // if (meshData.currentApp === meshData.destinationApp) {
  //   const errorMsg = `Mesh storage aborted: currentApp and destinationApp cannot be the same (${meshData.currentApp}). This indicates a logic error in mesh transfer.`;
  //   console.error(errorMsg);
  //   throw new Error(errorMsg);
  // }

  // Validation: Check if destinationApp is still set to 'none' (user hasn't selected a destination)
  if (meshData.destinationApp === 'none') {
    const errorMsg = `Mesh storage aborted: destinationApp is still 'none'. User must select a destination app before saving.`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  const database = await transferDb;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  // Check if mesh already exists
  const existingMesh = await meshIndex.get(meshData.meshId);
  
  if (existingMesh) {
    // Update existing mesh - set properties manually to ensure correct typing
    existingMesh.meshName = meshData.meshName;
    existingMesh.authorId = meshData.authorId;
    existingMesh.lastApp = meshData.currentApp;  // Update lastApp to current app from incoming data
    existingMesh.destinationApp = meshData.destinationApp;  // Set new destination
    existingMesh.stlData = meshData.stlData;
    existingMesh.lastSave = new Date();
    
    // Set autoLoad flag if provided
    if (autoLoad !== undefined) {
      existingMesh.autoLoad = autoLoad;
    }
    
    await store.put(existingMesh);
    await tx.done;
    devlog(`Updated existing mesh ${meshData.meshId} from ${meshData.currentApp} to ${meshData.destinationApp}`);
    return existingMesh;
  } else {
    // Create new mesh without including currentApp
    // Extract all properties except currentApp (which becomes lastApp)
    const { currentApp, ...restOfMeshData } = meshData;
    
    // Create the StoredMeshData object with proper naming
    const newMesh: Omit<StoredMeshData, 'id'> = {
      ...restOfMeshData,
      lastApp: currentApp,  // currentApp from TransferSaveData becomes lastApp in StoredMeshData
      lastSave: new Date(),
      autoLoad: autoLoad ?? false  // Default to false if not provided
    };
    
    const id = await store.add(newMesh) as number;
    await tx.done;
    
    devlog(`Created new mesh ${meshData.meshId} from ${currentApp} to ${meshData.destinationApp}`);
    return {
      ...newMesh,
      id,
    };
  }
};

/**
 * Sets the destination app for a mesh, updating the last app in the process
 * lastApp becomes the previous destinationApp value
 * @param meshId - The ID of the mesh to update
 * @param nextApp - The app ID to set as the new destination
 * @param autoLoad - Optional flag to indicate if this mesh should be automatically loaded
 */
export const setNewApp = async (meshId: MeshID, nextApp: AppID, autoLoad?: boolean): Promise<void> => {
  const database = await transferDb;
  if (!database) return;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  if (mesh) {
    // Update lastApp to the current destinationApp before changing it
    // Only do this if the current destinationApp is not the default value
    if (mesh.destinationApp !== NO_PREVIOUS_APP) {
      devlog(`Updating mesh ${meshId}: lastApp from ${mesh.lastApp} to ${mesh.destinationApp}`);
      mesh.lastApp = mesh.destinationApp;
    }
    
    devlog(`Setting mesh ${meshId} destinationApp to ${nextApp}`);
    mesh.destinationApp = nextApp;
    mesh.lastSave = new Date(); // Update the save timestamp
    
    // Set autoLoad flag if provided
    if (autoLoad !== undefined) {
      devlog(`Setting mesh ${meshId} autoLoad to ${autoLoad}`);
      mesh.autoLoad = autoLoad;
    }
    
    await store.put(mesh);
  }
  await tx.done;
};

/**
 * Deletes a specific mesh from the database
 */
export const deleteMesh = async (meshId: MeshID): Promise<void> => {
  const database = await transferDb;
  if (!database) return;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  if (mesh) {
    await store.delete(mesh.id);
    devlog(`Deleted mesh ${meshId}`);
  }
  await tx.done;
};

/**
 * Deletes all meshes from the database
 */
export const deleteAllStoredTransfers = async (): Promise<void> => {
  const database = await transferDb;
  if (!database) return;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  
  // Count records before delete
  const countBefore = await store.count();
  devlog(`Clearing IndexedDB store: ${MESH_STORE_NAME} (${countBefore} records)`);
  
  await store.clear();
  await tx.done;
  
  // Verify clear worked by checking count after
  const verifyTx = database.transaction(MESH_STORE_NAME, 'readonly');
  const verifyStore = verifyTx.objectStore(MESH_STORE_NAME);
  const countAfter = await verifyStore.count();
  devlog(`IndexedDB clear result: ${countAfter === 0 ? 'SUCCESS' : 'FAILED'} (${countAfter} records remaining)`);
};

/**
 * Deletes all meshes whose lastSave date is older than maxAgeMs milliseconds (default 24 h).
 * Operates across all apps in the shared transfer store.
 * Returns the number of meshes deleted.
 */
export const deleteStaleTransferMeshes = async (maxAgeMs = 24 * 60 * 60 * 1000): Promise<number> => {
  const database = await transferDb;
  if (!database) return 0;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const allMeshes = await store.getAll();
  const cutoff = Date.now() - maxAgeMs;
  const stale = allMeshes.filter(m => m.lastSave instanceof Date && m.lastSave.getTime() < cutoff);
  for (const mesh of stale) {
    await store.delete(mesh.id);
    devlog(`[DB] Deleted stale mesh ${mesh.meshId} (lastSave: ${mesh.lastSave.toISOString()})`);
  }
  await tx.done;
  return stale.length;
};

/**
 * Checks for active meshes in a specific app and returns the latest saved one
 * Returns both the latest mesh and the total count of meshes found
 * If onlyLatest is false, returns all meshes for the app in the meshes array
 * If findAutoLoad is true, will prioritize returning meshes with autoLoad=true
 */
export const checkForStoredMeshData = async (
  app: AppID,
  onlyLatest: boolean = true,
  findAutoLoad: boolean = false
): Promise<{
  latestMesh: StoredMeshData | null;
  autoLoadMesh: StoredMeshData | null;
  totalCount: number;
  meshes?: StoredMeshData[]
}> => {
  const database = await transferDb;
  if (!database) return { latestMesh: null, autoLoadMesh: null, totalCount: 0, meshes: [] };
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  
  // Get all meshes for this app
  const allMeshes = await store.getAll();
  const appMeshes = allMeshes.filter(mesh => mesh.destinationApp === app);
  
  if (appMeshes.length === 0) {
    return { 
      latestMesh: null, 
      autoLoadMesh: null,
      totalCount: 0, 
      meshes: [] 
    };
  }
  
  // Sort by lastSave and get the latest
  const sortedMeshes = appMeshes.sort((a, b) => b.lastSave.getTime() - a.lastSave.getTime());
  
  // Find mesh marked for auto-loading (if requested)
  let autoLoadMesh = null;
  if (findAutoLoad) {
    autoLoadMesh = sortedMeshes.find(mesh => mesh.autoLoad === true) || null;
    if (autoLoadMesh) {
      devlog(`Found autoLoad mesh for ${app}: ${autoLoadMesh.meshId}`);
    }
  }
  
  return {
    latestMesh: sortedMeshes[0],
    autoLoadMesh,
    totalCount: appMeshes.length,
    meshes: onlyLatest ? undefined : sortedMeshes
  };
};

/**
 * Retrieves the STL data for a specific mesh
 */
export const stlFromDB = async (meshId: MeshID): Promise<Blob | null> => {
  const database = await transferDb;
  if (!database) return null;
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  return mesh?.stlData ?? null;
};

/**
 * Retrieves the sender app (lastApp) for a specific mesh
 * @param meshId - The ID of the mesh to get the sender app for
 * @returns The app ID that sent this mesh, or null if mesh not found
 */
export const getSenderApp = async (meshId: MeshID): Promise<AppID | null> => {
  const database = await transferDb;
  if (!database) return null;
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');

  const mesh = await meshIndex.get(meshId);
  return mesh?.lastApp ?? null;
};

// =============================================================================
// Project Storage Functions
// =============================================================================

/**
 * Stored project with flattened fields for IndexedDB indexing
 */
interface StoredProject extends Project {
  id: string;  // Flattened from metadata.id for keyPath
  appId: string;  // Flattened from metadata.appId for index
  updatedAt: Date;  // Flattened from metadata.updatedAt for index
}

/**
 * Convert Project to StoredProject (flatten fields for IndexedDB)
 */
function toStoredProject(project: Project): StoredProject {
  return {
    ...project,
    id: project.metadata.id,
    appId: project.metadata.appId,
    updatedAt: project.metadata.updatedAt
  };
}

/**
 * Convert StoredProject back to Project (remove flattened fields)
 */
function fromStoredProject(stored: StoredProject): Project {
  const { id, appId, updatedAt, ...rest } = stored;
  return rest as Project;
}

/**
 * Stores or updates a project in the database
 * If a project with the same ID exists, it will be updated
 * Otherwise, a new project entry will be created
 * @param project - The project to store
 * @returns The stored project
 */
export const storeProject = async (project: Project): Promise<Project> => {
  const database = await localDb;
  if (!database) return project;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readwrite');
  const store = tx.objectStore(PROJECTS_STORE_NAME);

  const storedProject = toStoredProject(project);
  devlog('[DB] Storing project:', storedProject.id, storedProject.metadata.name);

  // Check if project already exists
  const existingProject = await store.get(storedProject.id);

  if (existingProject) {
    // Update existing project
    await store.put(storedProject);
    devlog(`Updated existing project ${storedProject.id}: ${storedProject.metadata.name}`);
  } else {
    // Create new project
    await store.add(storedProject);
    devlog(`Created new project ${storedProject.id}: ${storedProject.metadata.name}`);
  }

  await tx.done;
  return project;
};

/**
 * Gets all projects for a specific app
 * @param appId - The app ID to filter by
 * @returns Array of projects for the app, sorted by updatedAt descending
 */
export const getProjectsForApp = async (appId: AppID): Promise<Project[]> => {
  const database = await localDb;
  if (!database) return [];
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readonly');
  const store = tx.objectStore(PROJECTS_STORE_NAME);
  const appIndex = store.index('appId');

  const storedProjects = await appIndex.getAll(appId) as StoredProject[];
  devlog('[DB] Retrieved', storedProjects.length, 'projects for app', appId);

  // Convert back to Project and sort by updatedAt descending
  return storedProjects
    .map(fromStoredProject)
    .sort((a, b) => {
      const aTime = new Date(a.metadata.updatedAt).getTime();
      const bTime = new Date(b.metadata.updatedAt).getTime();
      return bTime - aTime;
    });
};

/**
 * Gets a project by its ID
 * @param projectId - The project ID to look up
 * @returns The project if found, null otherwise
 */
export const getProjectById = async (projectId: ProjectID): Promise<Project | null> => {
  const database = await localDb;
  if (!database) return null;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readonly');
  const store = tx.objectStore(PROJECTS_STORE_NAME);

  const storedProject = await store.get(projectId) as StoredProject | undefined;
  if (!storedProject) return null;
  return fromStoredProject(storedProject);
};

/**
 * Deletes a project from the database
 * @param projectId - The ID of the project to delete
 */
export const deleteProject = async (projectId: ProjectID): Promise<void> => {
  const database = await localDb;
  if (!database) return;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readwrite');
  const store = tx.objectStore(PROJECTS_STORE_NAME);

  await store.delete(projectId);
  devlog(`Deleted project ${projectId}`);
  await tx.done;
};

/**
 * Updates an existing project
 * @param project - The project with updated data
 * @returns The updated project
 */
export const updateProject = async (project: Project): Promise<Project> => {
  const database = await localDb;
  if (!database) return project;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readwrite');
  const store = tx.objectStore(PROJECTS_STORE_NAME);

  const storedProject = toStoredProject(project);
  await store.put(storedProject);
  devlog(`Updated project ${storedProject.id}: ${storedProject.metadata.name}`);
  await tx.done;
  return project;
};

/**
 * Gets the count of projects for a specific app
 * @param appId - The app ID to count projects for
 * @returns The number of projects
 */
export const getProjectCountForApp = async (appId: AppID): Promise<number> => {
  const database = await localDb;
  if (!database) return 0;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readonly');
  const store = tx.objectStore(PROJECTS_STORE_NAME);
  const appIndex = store.index('appId');

  return await appIndex.count(appId);
};

/**
 * Deletes all projects for a specific app
 * Useful for debugging/testing
 * @param appId - The app ID to delete projects for
 */
export const deleteAllProjectsForApp = async (appId: AppID): Promise<number> => {
  const database = await localDb;
  if (!database) return 0;
  const tx = database.transaction(PROJECTS_STORE_NAME, 'readwrite');
  const store = tx.objectStore(PROJECTS_STORE_NAME);
  const appIndex = store.index('appId');

  const projects = await appIndex.getAll(appId) as StoredProject[];
  let deleted = 0;

  for (const project of projects) {
    await store.delete(project.id);
    deleted++;
    devlog(`Deleted project ${project.id}: ${project.metadata.name}`);
  }

  await tx.done;
  devlog(`Deleted ${deleted} projects for app ${appId}`);
  return deleted;
};

// ============================================================================
// Prefs — small key/value settings that outlive the session
// ============================================================================
//
// IndexedDB rather than localStorage because GrabCAD Print's embedded webview
// doesn't reliably keep localStorage across separate launches of the app. Reads
// go straight to IndexedDB, so they're local and instant.
//
// IndexedDB alone isn't enough under GrabCAD, though: it gets cleared between
// launches too, so a pref set in one session is gone by the next. Where the host
// offers a store of its own, a backend registered in prefsBackend.ts mirrors the
// set into it. Writes go to IndexedDB first and push to the host in the
// background; the host's copy is pulled back into IndexedDB once at startup, as
// soon as the env is known. By the time app code reads a pref, it's usually
// already local — and a read that gets in early waits for the pull rather than
// wrongly reporting "never set".
//
// Values that were already in localStorage are migrated on first read, so a
// preference set by an older build carries over. localStorage is also the
// fallback where IndexedDB isn't available at all (iOS Lockdown Mode) — no
// worse than what these used to do.

/**
 * Longest a read will wait on the startup pull before answering from what's
 * local. Only a read that misses pays it, and only until the pull settles.
 * Well under the backend's own timeout on purpose: a splash screen deciding
 * whether to open is the caller this exists for, and a modal that appears
 * seconds late is worse than one that appears when it shouldn't have.
 */
const PREF_HYDRATE_WAIT_MS = 2_000;

/** Keys written during this session. They win over anything the startup pull
 *  brings back — a box ticked while the pull is still in flight must not be
 *  undone by the host's older copy. */
const locallyWritten = new Set<string>();

/** Every key known to be a pref. Only needed where IndexedDB is unavailable and
 *  prefs share localStorage with everything else on the origin, leaving no
 *  store to enumerate. */
const seenPrefKeys = new Set<string>();

/** The backend for this env, once the env is known. Undefined means there is
 *  none and IndexedDB is the whole story. */
let prefsHost: PrefsBackend | undefined;

/** Every pref currently in the local store. This, rather than a tally of what
 *  this session wrote, is what gets pushed to the host: it includes prefs from
 *  earlier sessions, so a push can never narrow the set. */
async function allLocalPrefs(): Promise<Record<string, string>> {
  const database = await prefsDb;
  if (database) {
    const records = await database.getAll(PREFS_STORE_NAME) as { key: string; value: string }[];
    return Object.fromEntries(records.map(r => [r.key, r.value]));
  }
  const stored: Record<string, string> = {};
  for (const key of seenPrefKeys) {
    const value = localStorage.getItem(key);
    if (value !== null) stored[key] = value;
  }
  return stored;
}

/**
 * Pulls the host's prefs into IndexedDB. Fills gaps only — a key already held
 * locally was either written this session or written and pushed by an earlier
 * one, so the local value is at least as fresh as the host's.
 */
async function hydratePrefs(): Promise<void> {
  await envReady;
  prefsHost = getPrefsBackend(internalData.appEnv);
  if (!prefsHost) return;

  const remote = await prefsHost.loadAll();
  if (!remote) return;

  const database = await prefsDb;
  let adopted = 0;
  for (const [key, value] of Object.entries(remote)) {
    seenPrefKeys.add(key);
    if (locallyWritten.has(key)) continue;
    if (database) {
      if (await database.get(PREFS_STORE_NAME, key)) continue;
      await database.put(PREFS_STORE_NAME, { key, value });
    } else {
      if (localStorage.getItem(key) !== null) continue;
      localStorage.setItem(key, value);
    }
    adopted++;
  }
  devlog('[DB] Pulled', Object.keys(remote).length, 'prefs from the host store,', adopted, 'adopted');
}

/**
 * Resolves once the host's prefs are in the local store — immediately after the
 * env settles where there's no backend. Never rejects: a host that can't answer
 * leaves prefs exactly as they were.
 */
export const prefsReady: Promise<void> = hydratePrefs()
  .catch(err => { console.warn('[DB] Pref hydration failed:', err); });

/** Serialises host pushes so two quick writes can't land out of order. */
let hostPushChain: Promise<void> = Promise.resolve();

/**
 * Sends the whole pref set to the host, in the background. Deliberately not
 * awaited by the caller: the local write is what the rest of the session reads,
 * and the host's acknowledgement can take seconds — or never come at all, on a
 * host that doesn't implement the commands.
 */
function pushPrefsToHost(): void {
  hostPushChain = hostPushChain
    .then(() => prefsReady)   // don't push over the host's copy before reading it
    .then(async () => { if (prefsHost) await prefsHost.saveAll(await allLocalPrefs()); })
    .catch(err => { console.warn('[DB] Pref push to host failed:', err); });
}

/** Reads a preference. Null if never set. */
export const getPref = async (key: string): Promise<string | null> => {
  const database = await prefsDb;
  const read = async () => database
    ? (await database.get(PREFS_STORE_NAME, key) as { key: string; value: string } | undefined)?.value ?? null
    : localStorage.getItem(key);

  const local = await read();
  if (local !== null) return local;

  // Adopt any value an older, localStorage-backed build left. Checked before
  // the wait below because it costs nothing.
  if (database) {
    const legacy = localStorage.getItem(key);
    if (legacy !== null) {
      devlog('[DB] Migrating pref from localStorage:', key);
      await setPref(key, legacy);
      return legacy;
    }
  }

  // Nothing local. The startup pull may still be in flight — and on a host that
  // wipes IndexedDB between launches, that pull is the only thing standing
  // between "never set" and a pref the user set in an earlier session.
  await Promise.race([
    prefsReady,
    new Promise<void>(resolve => setTimeout(resolve, PREF_HYDRATE_WAIT_MS)),
  ]);
  return read();
};

/** Writes a preference. */
export const setPref = async (key: string, value: string): Promise<void> => {
  locallyWritten.add(key);
  seenPrefKeys.add(key);
  const database = await prefsDb;
  if (!database) {
    localStorage.setItem(key, value);
  } else {
    await database.put(PREFS_STORE_NAME, { key, value });
    devlog('[DB] Stored pref:', key, '=', value);
  }
  pushPrefsToHost();
};

/** Removes a preference, in both the DB and any legacy localStorage copy. */
export const deletePref = async (key: string): Promise<void> => {
  locallyWritten.add(key);
  seenPrefKeys.add(key);
  localStorage.removeItem(key);
  const database = await prefsDb;
  if (database) {
    await database.delete(PREFS_STORE_NAME, key);
    devlog('[DB] Deleted pref:', key);
  }
  pushPrefsToHost();
};

// /**
//  * Creates a test mesh for restore testing
//  * Sets lastApp to 'test' and destinationApp to the provided appId
//  * @param appId - The app ID to set as destinationApp
//  * @param meshData - Optional mesh data to include (stlData, meshName, etc.)
//  */
// export const createRestoreTest = async (appId: AppID, meshData?: Partial<Omit<TransferSaveData, 'currentApp'>>): Promise<StoredMeshData> => {
//   const testMesh: TransferSaveData = {
//     authorId: 'test',
//     meshName: 'Restore Test Mesh',
//     meshId: `restore-test-${appId}-${Date.now()}`,
//     currentApp: meshData?.currentApp,  // Set current app
//     stlData: null,
//     destinationApp: appId,
//     ...meshData  // Override defaults with provided mesh data
//   };

//   return storeMesh(testMesh);
// };